import 'disposablestack/auto';
import $ from 'jquery';
import {dragdrop1d, EL, playSound, renderIcon2} from './util/DomUtils';
import {DomWindow, DomWindowConfig } from './DomWindow';
import { UiContext } from './util/UiContext';
import {CancelledError, clamp, DateRange, splitInTwoParts} from './util/CoreUtils';
import { ObjectTreeCtrl } from "./GridCtrl";
import {Action, Command, CommandProperties} from "./ActionManager";

var currentDomMenu : Disposable = null;

export type MessageBarAction = [string, EventListener];
export class MessageBar {
    private static barsVisible = {};
    private static barsTimeout = {};
    private static elements : {[key: string]: HTMLDivElement} = {};
    static show(className:string, text:string, timeout:number, actions: MessageBarAction[] = []) {
        if (!(className in MessageBar.elements)) {
            MessageBar.elements[className] = EL('div', {class:`messageBar ${className} wndStack`, id:`loadingWidget_${className}`, prependTo: document.body});
            dragdrop1d(MessageBar.elements[className], 'y', [{
                button: 1, condition: (o, s) => true,
                init: (x) => { window.clearTimeout(this.barsTimeout[className]); return el.clientHeight; },
                transform: (x, diff) => el.style.height = (x - 2*diff) + 'px',
                finishMove: (x, diff) => {
                    if(diff > 10) { MessageBar.hide(className); return; } else if (diff < -10) UiContext.instance.sel?.toolbarClick(new Command({ clickAppMethod: 'showWindow', param: '"chat"' }));
                    el.style.height = ''
                }, abortMove: () => el.style.height = '', allowDefault: true, otherAxisCancelRatio: 0.5,
            }])
        }
        const el = MessageBar.elements[className];
        el.style.height = ''
        bringToFrontWndStack(el);
        if (UiContext.instance.markdown)
            el.innerHTML = UiContext.instance.markdown.render(text, true);
        else
            el.innerText = text;
        for(const [title, action] of actions) el.append(EL("a", {'class':'actionItem', 'onclick': action}, title));
        $(el).slideDown();
        if (localStorage.enableAudio && !this.barsVisible[className])
            playSound(className == 'error' ? 'chord' : 'ding');
        this.barsVisible[className] = true;
        if (timeout) {
            try { window.clearTimeout(this.barsTimeout[className]); } catch {}
            this.barsTimeout[className] = setTimeout(function() { MessageBar.hide(className); }, timeout);
        }
    }
    static hide(className:string) {
        $(MessageBar.elements[className]).slideUp();
        this.barsVisible[className] = false;
    }
}

export const WNDSTACK_SELECTOR =  'body > .wndStack';
export function getTopMostElement(selector : string) {
    const elements = getElementsOrderedByZIndex(selector);
    return elements[elements.length - 1];
}
export function getElementsOrderedByZIndex(selector : string) {
    const elements : HTMLElement[] = Array.prototype.slice.call(document.querySelectorAll(selector), 0);
    elements.sort((a : HTMLElement, b : HTMLElement) => (+a.style.zIndex) - (+b.style.zIndex));
    return elements;
}
export function bringToFront(selector : string, zIndexOffset : number, element : HTMLElement) {
    const elements = getElementsOrderedByZIndex(selector);
    const index = elements.indexOf(element);
    if (index > -1) { // only splice elements when item is found
      elements.splice(index, 1); // 2nd parameter means remove one item only
    }
    elements.push(element);
    elements.forEach((el, index) => {
        el.style.zIndex = (zIndexOffset + index).toString();
    });
}
export const bringToFrontWndStack : (element: HTMLElement) => void =
    bringToFront.bind(null, WNDSTACK_SELECTOR, 120000);

function registerClearMenuHandlers() {
    document.addEventListener("click", function(e) {
        if (currentDomMenu && !(e.target as HTMLElement).closest("#currentDomMenu")) {
            console.log("clearing currentDomMenu",currentDomMenu);
            currentDomMenu[Symbol.dispose](); e.preventDefault();
        }
    }, true);
    document.addEventListener("contextmenu", function(e) {
        if (currentDomMenu && !(e.target as HTMLElement).closest("#currentDomMenu")) {
            currentDomMenu[Symbol.dispose](); e.preventDefault();
        }
    }, true);
}

function centerElementOnPage(element : HTMLElement) {
    if (element.clientWidth > window.innerWidth-10) element.style.width = (window.innerWidth-10) + 'px';
    if (element.clientHeight > window.innerHeight-50) element.style.height = (window.innerHeight-50) + 'px';
    element.style.left = Math.max(0, (window.innerWidth - element.clientWidth) / 2) + "px";
    element.style.top = Math.max(0, (window.innerHeight - element.clientHeight) / 2) + "px";
}

async function toolbarContextmenu(e, item) {
    e?.preventDefault();
    const button = await ObjectTreeCtrl.showDialog(item, undefined, undefined, {
        buttons: (item?.document && item?.id && UiContext.instance.sel) ? ['🧭'] : [],
        push: true,
    });
    if (button === '🧭') UiContext.instance.sel.execute(Action.parse('app.navigate document,id'), null, [{props: item}]);
}

type DateTextItem = { text: string, date?: Date };
export async function ShowRelTimePicker(baseDate: Date, documentDateRange?: DateRange, menuItems: (string|Date|DateTextItem)[] = [], options: SearchableMenuOptions = undefined): Promise<Date> {
    const df = d => d.toLocaleString('fr-FR', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric', hour: 'numeric', minute: 'numeric', second: 'numeric'});
    let m = search => menuItems.mapDefined(x => {
        if (x instanceof Date) return { text: `${df(x)}`, date: x } ;
        if (typeof x === 'string' ) {
            try {
                if (search) x = `${search} ${x}`;
                const result = Date.parseRelDatetime(x, documentDateRange, baseDate);
                return { text: x, secondaryText: `${df(result)}`, date: result };
            } catch {
                return undefined;
            }
        }
        return x;
    });
    return (await SearchableMenu.show((search) => {
        try {
            if (!baseDate && !search) return m(search);
            const date = Date.parseRelDatetime(search, documentDateRange, baseDate);
            return [{ text: `${df(date)}`, date } as DateTextItem].concat(m(search));
        }catch(e) {
            return [{ text: `${e}`, disabled: true } as DateTextItem];
        }
    }, Object.assign({
        center: true,
        forResponse: true,
        cancelResponse: {},
        width: 400,
        placeholder: "",
        backdrop: true,
    }, options))).date;
}

export function ShowDialog(message : string | null, menuItems : MenuItemsParamType, options: SearchableMenuOptions = undefined): Promise<any> {
    return SearchableMenu.show(menuItems, Object.assign({
        message,
        center: true,
        forResponse: true,
        cancelResponse: {},
        width: 400,
        placeholder: "",
        backdrop: true,
    }, options));
}

export function ShowDropdown(event : MouseEvent, menuItems : MenuItemsParamType, options: SearchableMenuOptions = undefined) {
    return SearchableMenu.show(menuItems, Object.assign({ event, noFocusOnMobile: true }, options));
}

var mod = function (a, n) {
    "use strict";
    return ((a % n) + n) % n;
};

export type SearchableMenuOptions = {
    /**
     * A callback that's run when the user selects an item. If this is not set, `forResponse` may be set, or each menuItem
     * may contain its own `action` property containing such a callback.
     */
    action?: (this: HTMLDivElement, e: MouseEvent, item: any) => void,
    /**
     * If this is set, once an item is selected, the Promise returned by `SearchableMenu.show` (and stored in
     * `result`) will resolve with the selected item.
     */
    forResponse?: boolean,
    /**
     * If this is set, the `result` Promise won't reject, ever, even if the menu is closed without selecting an item,
     * and will instead resolve with the value specified herein.
     */
    cancelResponse?: any,
    /**
     * Text to display in the search box if it's empty.
     *
     * @deprecated use MenuSource property
     */
    placeholder?: string,
    /**
     * Initial text to place in the search bar.
     */
    initialSearch?: string,
    /**
     * Store last search term internally under this key, re-use when opened again with same key.
     *
     * In a future version, we may store a history of last used search terms, instead of only the last one.
     */
    mruKey?: string,
    /**
     * Minimum width of the menu.
     */
    width?: number,
    /**
     * If specified, the menu will be rendered as a context menu next to the mouse cursor.
     */
    event?: MouseEvent,
    /**
     * If specified, the menu will be centered in the middle of the screen, instead of being centered horizontally but in a fixed place vertically.
     */
    center?: boolean,
    /**
     * Title to display above the search box, large font size.
     */
    title?: string,
    /**
     * Message to display above the search box, regular font size.
     */
    message?: string,
    /**
     * Show a backdrop, "graying out" the rest of the page. Makes more obvious to the user that this is a modal dialog.
     */
    backdrop?: boolean,
    /**
     * Adds a dynamic search result containing exactly the user's input in the search box, allowing the user to type in
     * arbitrary text instead of selecting one of the predefined results.
     *
     * @deprecated use MenuSource property
     */
    allowFreeText?: any,
    /**
     * Debounce the search. Only call the menuItems callback once every debounceMs milliseconds. Recommended if the callback
     * performs any long-running or network tasks.
     */
    debounceMs?: number,
    /**
     * Don't focus the search box on mobile. Recommended for context menus, where it's usually unwanted to open the on-screen keyboard automatically.
     */
    noFocusOnMobile?: boolean,
    /**
     * Render a custom HTML element instead of the list of search results. Allows to use the backdrop and related event infrastructure
     * of SearchableMenu to be used for a simple modal dialog of any content.
     */
    customElement?: HTMLElement | Promise<HTMLElement> | null,
    /**
     * Display custom buttons next to the title of the modal. Array of button labels.
     *
     * If a button is clicked, the dialog is closed and the `result` promise resolves with the label of the button.
     */
    buttons?: string[],
    /**
     * If set to 'below', buttons are displayed as footer.
     */
    buttonMode?: string,
    /**
     * Keep current menu open, show this one on top.
     */
    push?: boolean,
    /**
     * if multiple MenuSources are given, use this parameter to set the default one. 0 if not set.
     */
    defaultSource?: number,
    /**
     * close menu once an item is clicked. defaults to false in case a global action is provided, defaults to true if an action per item is provided, forced to true if forResponse is set
     */
    closeOnClick?: boolean,
};
export type MenuItem = {[key: string]: any};
export type MenuSearchFn = ((search: string, cancel: AbortSignal) => MenuItem[] | AsyncIterable<MenuItem>) | ((search: string, cancel: AbortSignal) => Promise<MenuItem[]>);
export type MenuSource = ({ static: MenuItem[] | (() => Promise<MenuItem[]>) } | { fn: MenuSearchFn } /*| { customElement: HTMLElement | Promise<HTMLElement> }*/) & {
    label?: string,
    prefix?: string,
    bang?: string,
    helptext?: string,
    placeholder?: string,
    /**
     * Adds a dynamic search result containing exactly the user's input in the search box, allowing the user to type in
     * arbitrary text instead of selecting one of the predefined results.
     */
    allowFreeText?: any,
};
export type MenuItemsParamType = MenuSource[] | MenuItem[] | MenuSearchFn;
export class SearchableMenu implements Disposable {
    menu? : JQuery;
    menuWrapper : JQuery;
    menuSources : MenuSource[];
    selectedSource : MenuSource;
    matchedSource? : MenuSource = null;
    promise : { resolve: (any) => void, reject: () => void };
    raceCtr = 0;
    options: Readonly<SearchableMenuOptions>;
    $search? : JQuery;
    selectedIndex = 0;
    result : Promise<any>;
    backdropEl : HTMLDivElement;
    parentMenu : Disposable | undefined;
    cancellation : AbortController | undefined;
    private menuSourceSelect : HTMLDivElement | undefined;
    private debounceTimer;
    private currentOverlay: HTMLDivElement | undefined;

    static DISABLED = Symbol('DISABLED');
    static EXTRA_CLASSES = Symbol('EXTRA_CLASSES');

    /*
    static set currentMenu (value: Disposable) {
        if (currentDomMenu) {
            const cur = currentDomMenu;
            currentDomMenu = null;
            cur[Symbol.dispose]();
        }
        if (value) currentDomMenu = value;
    }
    */
    static get currentMenu(): Disposable {
        return currentDomMenu;
    }

    static renderMenuItem(item: any, dest: HTMLElement, hasGlobalAction: boolean, insertMode = 'insertBefore') {
        const hotkeys: string[] = item.hotkey ? (item.hotkey instanceof Array ? item.hotkey : item.hotkey.split(/;;/)) : [];
        var el = EL(
            'div', {'data-text': item.text, [insertMode]: dest},
            ''+item.text||'',
            item.contentEl,
            hotkeys.length ? EL('span', {class:'hotkeys'}, ...hotkeys.map(hotkey => EL('kbd', {}, hotkey))) : undefined,
            item.secondaryText ? EL('dl', {}, ...item.secondaryText.split(/;;/).flatMap(txt => {
                var [k, v] = splitInTwoParts(txt, '=');
                return [
                    EL('dt', {}, k), v ? EL('dd', {}, v) : undefined
                ];
            })) : undefined,
        );
        el['menuItem'] = item;
        if (hotkeys[0]?.startsWith('[') && hotkeys[0]?.endsWith(']')) el.accessKey = hotkeys[0].substring(1, hotkeys[0].length - 1);
        if (item.icon) renderIcon2(item.icon, 16, UiContext.instance.db, EL('span', {class:'icon', prependTo:el}));
        if (item.font) el.style.fontFamily = item.font;
        if (item.backgroundColor) el.style.borderRight = '5px solid '+ item.backgroundColor;
        if (item[SearchableMenu.EXTRA_CLASSES]) el.classList.add(...item[SearchableMenu.EXTRA_CLASSES]);

        if ((item.action || hasGlobalAction) && !item.disabled && !item[SearchableMenu.DISABLED])
            el.setAttribute('data-menuitem', '');
            //el.addEventListener('click', overrideAction);
        //else if (item.action && !item.disabled && !item[SearchableMenu.DISABLED])
            //el.addEventListener('click', function(e) { if (!e.shiftKey) currentDomMenu[Symbol.dispose]();  item.action.call(this, e); });
        else {
            el.classList.add("disabled");
            if (item.text === '-') el.classList.add('sep');
        }
        return el;
    }
    overlayBtns: [string, Function | null, Function][] = [
        ['🧭', item => (item?.document && item?.id && UiContext.instance.sel), (item, el, menu, e) => UiContext.instance.sel.execute(Action.parse('app.navigate document,id'), e, [{props: item}])],
        ['⚙️', item => (item?.document && item?.id && UiContext.instance.sel), (item, el, menu, e) => UiContext.instance.sel.execute(Action.parse('app.navigate "objectproperties:${document}/${id}"'), e, [{props: item}])],
        ['🕵️‍♀️', null, (item, el, menu) => toolbarContextmenu(null, item) ],
        //['📄', null, (item, el, menu) => navigator.clipboard.writeText(el.getAttribute('data-text')) ],
        ['⤴️', null, (item, el, menu) => menu.setSearchWithPrefix(el.getAttribute('data-text')) ],
    ]
    showOverlay(item, el, event) {
        if (this.currentOverlay) {
            this.currentOverlay.remove();
        }
        this.currentOverlay = EL('div', {class:'overlay', prependTo: el, onclick: e => {e.stopPropagation(); this.currentOverlay.remove()}},
            EL('div', {class:'buttons'}, ...this.overlayBtns.filter(([_, check, __]) => !check || check(item)).map(([lbl, _, call],i) => EL('button', { onclick:e => { e.stopPropagation(); this.currentOverlay.remove(); call(item, el, this, e) }}, lbl))));
        event.preventDefault();
    }
    async renderMenu() {
        if (this.cancellation) this.cancellation.abort();
        let source = this.selectedSource;
        let checkFor = this.$search.val() as string;
        this.matchedSource = null;
        for(const s of this.menuSources) {
            if (s !== this.selectedSource && s.prefix && checkFor.startsWith(s.prefix)) {
                source = s;
                checkFor = checkFor.substring(s.prefix.length);
                this.matchedSource = s;
                break;
            }
        }
        if (typeof source.placeholder === 'string') this.$search.attr('placeholder', source.placeholder);
        if (this.menuSourceSelect) {
            this.menuSourceSelect.querySelector(".selected")?.classList.remove("selected");
            this.menuSourceSelect.children[this.menuSources.indexOf(source)].classList.add("selected");
        }

        const loadindicator = $("<div style='text-align: center'><div class='loadindicator small'><div></div><div></div><div></div></div></div>");
        this.menu[0].replaceChildren(loadindicator[0]);
        if (source.helptext) this.menu[0].appendChild(EL('div', {class: 'disabled'}, source.helptext));

        var curRaceCtr = ++this.raceCtr;
        const hasGlobalAction = !!this.options.action || this.options.forResponse;
        if (source.allowFreeText) SearchableMenu.renderMenuItem(Object.assign({ icon: 'plus_button.png', text: checkFor }, source.allowFreeText), this.menu[0], hasGlobalAction, 'appendTo');
        var items;
        if ('fn' in source) {
            this.cancellation = new AbortController();
            try {
                items = await source.fn(checkFor, this.cancellation.signal);
            } catch (ex) {
                // Ignore CancelledError and DOMException(name="AbortError")
                if (!(ex instanceof CancelledError) && ex?.name !== 'AbortError') throw ex;
            }
        } else if ('static' in source) {
            if (typeof source.static === 'function') {
                source.static = await source.static();
            }
            this._setMenuFilter(checkFor);
            items = source.static.filter(m => this.filterMenu(m.text));
        }
        if (curRaceCtr < this.raceCtr) return;
        let first = true;
        const handleItem = item => {
            if (!item) return;
            const el = SearchableMenu.renderMenuItem(item, loadindicator[0], hasGlobalAction);
            el.addEventListener('contextmenu', this.showOverlay.bind(this, item, el));
            if (first) { this.selectIndex(0); first = false; }
        };
        if (items instanceof Array) {
            items.forEach(handleItem);
        } else if (Symbol.asyncIterator in items) {
            for await(const item of items) {
                if (curRaceCtr < this.raceCtr) return;
                handleItem(item);
            }
        }
        if (first && source.allowFreeText) this.selectIndex(0);
        loadindicator.hide();
    }
    selectIndex(indexOrElement: number | HTMLDivElement) {
        var visibleItems = this.menu.children('div[data-menuitem]:not(.hide)');
        let i : number, item: JQuery;
        if (typeof indexOrElement === 'number') {
            i = mod(indexOrElement, visibleItems.length);
            item = visibleItems.eq(i);
        } else {
            i = visibleItems.index(indexOrElement);
            if (i === -1) return;
            item = $(indexOrElement);
        }
        if (item.length) {
            this.menu.find('div.selected').removeClass('selected');
            item.addClass('selected');
            if (typeof indexOrElement === 'number') item[0].scrollIntoView({block: "nearest", inline: "nearest"});
            this.selectedIndex = i;
        }
    }
    handleAction(el, event) {
        const item = el['menuItem'];
        if (this.options.action) {
            this.options.action.call(el, event, item);
            if (this.options.closeOnClick === true) currentDomMenu[Symbol.dispose]();
        } else if (this.options.forResponse) {
            this.promise.resolve(item);
            currentDomMenu[Symbol.dispose]();
        } else {
            if (!event.shiftKey && this.options.closeOnClick !== false) currentDomMenu[Symbol.dispose]();
            item.action.call(el, event, item);
        }
    }
    renderButtons() {
        return EL('div', {class:'buttons'}, ...(this.options.buttons || []).map((x,i) => EL('button', {accesskey: `${i+1}`, onclick:() => { this.handleButtonClick(x) }}, x)));
    }
    handleButtonClick(label: string) {
        this.promise.resolve(label); this[Symbol.dispose]();
    }

    /**
     * Display a searchable menu on screen, returning a promise that:
     * - resolves to the item the user selected or rejects if the menu is closed without selecting an item, e.g. by clicking outside (if forResponse is set)
     * - resolves to `null` as soon as the menu is closed by whatever means (if forResponse is not set).
     *
     * @param menuItems
     * @param options
     * @see SearchableMenuOptions
     */
    static show(menuItems : MenuItemsParamType, options: Readonly<SearchableMenuOptions> = undefined): Promise<any> {
        return SearchableMenu.create(menuItems, options).result;
    }
    static create(menuSources : MenuItemsParamType, options: Readonly<SearchableMenuOptions> = undefined): SearchableMenu {
        const sm = new SearchableMenu();
        sm.result = new Promise((resolve, reject) => {
            sm.promise = {resolve, reject};
            sm.options = options || {};
            if (sm.options.customElement === null || sm.options.customElement) {
                sm.menuSources = null;
            } else if (typeof menuSources === 'function') {
                sm.menuSources = [{ fn: menuSources, placeholder: sm.options.placeholder, allowFreeText: sm.options.allowFreeText }];
            } else if (menuSources instanceof Array && menuSources.length && menuSources.every(s => 'fn' in s || 'static' in s || 'customElement' in s)) {
                sm.menuSources = menuSources as MenuSource[];
            } else if (menuSources instanceof Array) {
                sm.menuSources = [{ static: menuSources, placeholder: sm.options.placeholder, allowFreeText: sm.options.allowFreeText }];
            } else {
                throw new Error("invalid menuSources specified " + typeof menuSources);
            }
            sm.selectedSource = sm.menuSources && sm.menuSources[sm.options.defaultSource || 0];
            if (sm.options.push) {
                sm.parentMenu = currentDomMenu;
                currentDomMenu = null;
            } else {
                while (currentDomMenu) currentDomMenu[Symbol.dispose](); // clear all other context menus
            }
            if (sm.options.backdrop || (DomWindowConfig.isMobileMode && !('backdrop' in sm.options))) {
                document.body.appendChild(sm.backdropEl = EL('div', {class:'contextmenuBackdrop', onclick: () => sm.clear()}));
            }
            sm.menuWrapper = $("<div class='contextmenuWrapper'></div>").appendTo(document.body);
            if (sm.options.title || sm.options.buttons) sm.menuWrapper.append(EL('div', {class:'title'}, sm.options.title,
                sm.options.buttonMode !== 'below' ? sm.renderButtons() : undefined
            ));
            if (sm.options.message) sm.menuWrapper.append(EL('div', {class:'message'}, sm.options.message));
            if (sm.options.title) sm.menuWrapper.addClass('hasTitle');
            if (sm.options.message) sm.menuWrapper.addClass('hasMessage');
            let renderPromise;
            if (sm.options.customElement === null || sm.options.customElement) {
                sm.menuWrapper.addClass('contentIsCustom')
                renderPromise = Promise.resolve(sm.options.customElement).then(el => sm.menuWrapper.append(sm.menu = $(el)));
                sm.menuWrapper.attr('tabindex', 0).on('keydown', (e) => { if (e.key === 'Escape') { e.stopPropagation(); sm.clear() } });
                sm.menuWrapper[0].focus({ preventScroll: true, focusVisible: false } as any /* types for FocusOptions seems outdated, misses focusVisible */);
            } else {
                if (sm.menuSources.length > 1 || sm.menuSources[0].label) {
                    sm.menuSourceSelect = EL('div', {class:'tab-bar menu-sources', appendTo: sm.menuWrapper[0]},
                        ...sm.menuSources.map(source => EL('button', {onclick: e => { sm.selectedSource = source; sm.renderMenu(); sm.focus(); }}, source.label)));
                }
                sm.menuWrapper.addClass('contentIsMenu')
                sm.$search = $("<input type='search' placeholder='Search ...'>").appendTo(sm.menuWrapper).on('input', function(this: HTMLInputElement, e) {
                    if (sm.options.mruKey && !sm.options.initialSearch) localStorage['SearchableMenu__'+sm.options.mruKey] = this.value;
                    if (sm.menuSources.length > 1 || 'fn' in sm.selectedSource || sm.options.allowFreeText) {
                        if (sm.options.debounceMs) {
                            if (sm.debounceTimer) clearTimeout(sm.debounceTimer);
                            sm.debounceTimer = setTimeout(() => sm.renderMenu(), sm.options.debounceMs);
                        } else {
                            sm.renderMenu();
                        }
                    } else {
                        sm._setMenuFilter(this.value);
                        sm.menu.find('div').each((_, div: HTMLDivElement) => {
                            if (this.value && div.accessKey.toLowerCase() === this.value.toLowerCase()) {
                                sm.handleAction(div, e);
                            }
                            if (sm.filterMenu(div.innerText)) div.classList.remove('hide'); else div.classList.add('hide');
                        });
                        sm.selectIndex(0);
                    }
                }).on('keydown', (e) => {
                    if (e.key == 'Escape') { sm[Symbol.dispose](); e.stopPropagation(); e.preventDefault(); }
                    if (e.key == 'Enter') { sm.handleAction(sm.menu.find('div.selected')[0], e); e.stopPropagation(); e.preventDefault(); }
                    if (e.key == 'ArrowUp') { sm.selectIndex(sm.selectedIndex - 1); e.preventDefault(); }
                    if (e.key == 'ArrowDown') { sm.selectIndex(sm.selectedIndex + 1); e.preventDefault(); }
                    if (e.key == 'Tab') { sm.setSearchWithPrefix(sm.menu.find('div.selected').attr('data-text')); e.preventDefault(); }
                }).on('click', function(e) { e.stopPropagation(); });
                if (sm.options.initialSearch) sm.$search.val(sm.options.initialSearch).select();
                else if (sm.options.mruKey) sm.$search.val(localStorage['SearchableMenu__'+sm.options.mruKey] || '').select();
                sm.menu = $("<div class='contextmenu'></div>").appendTo(sm.menuWrapper).on('click', function(e) {
                    const menuitem = e.target.closest("[data-menuitem]");
                    if (menuitem) sm.handleAction(menuitem, e);
                });

                let lastMouseMoveTarget = null;
                sm.menu.on('mousemove', (e) => {
                    let targ = e.target;
                    while (targ && targ.tagName !== 'DIV') targ = targ.parentElement;
                    if (!targ || !targ.hasAttribute('data-menuitem')) return;
                    if (targ === lastMouseMoveTarget) return;
                    sm.selectIndex(targ as HTMLDivElement);
                    lastMouseMoveTarget = targ;
                });
                renderPromise = sm.renderMenu();
            }
            if (sm.options.buttons && sm.options.buttonMode === 'below') sm.menuWrapper.append(sm.renderButtons());
            if ((!DomWindowConfig.isMobileMode || !sm.options.noFocusOnMobile) && sm.menuWrapper.find('input').length)
                (sm.menuWrapper.find('input')[0] as HTMLInputElement).focus({ preventScroll: true });

            if (sm.options.event) {
                sm.menuWrapper.addClass('isContextMenu');
                renderPromise.then(() => {
                    var left = sm.options.event.clientX;
                    var top = sm.options.event.clientY;
                    if (top > window.innerHeight - sm.menuWrapper[0].clientHeight-15 && top > window.innerHeight/2) {
                        top = Math.max(0, top - sm.menuWrapper[0].clientHeight); sm.menuWrapper.css('height', sm.menuWrapper[0].clientHeight);
                    }
                    if (options.width) sm.menuWrapper.css('width', Math.min(options.width, window.innerWidth));
                    if (left + sm.menuWrapper[0].clientWidth > window.innerWidth) left = window.innerWidth - sm.menuWrapper[0].clientWidth ;
                    sm.menuWrapper.css({ top: top + "px", left: left + "px" });
                    const titleHeight = sm.menuWrapper[0].clientHeight - sm.menu[0].clientHeight;
                    sm.menu.css({ maxHeight: (window.innerHeight - top - 15 - titleHeight) + "px" });
                });
            } else if (sm.options.center && !DomWindowConfig.isMobileMode) {
                sm.menuWrapper.addClass('isCentered').addClass('isModal');
                if (sm.options.width) sm.menuWrapper.css("width", Math.min(options.width, window.innerWidth));
                renderPromise.then(() => {
                    centerElementOnPage(sm.menuWrapper[0]);
                    sm.menu.css('height', sm.menu[0].clientHeight);
                });
            } else {
                sm.menuWrapper.addClass('isModal');
                sm.menuWrapper.css({
                    top: (DomWindowConfig.isMobileMode ? 40 : 120) + "px",
                    width: DomWindowConfig.isMobileMode ? (sm.options.center ? "90%" : "100%") : (sm.options.width || "400px"),
                });
                sm.menuWrapper.css({
                    left: (window.innerWidth - sm.menuWrapper[0].clientWidth) / 2 + "px",
                });
                renderPromise.then(() => {
                    sm.menu.css({ maxHeight: window.innerHeight - 200 + "px", overflowY: "auto" });
                });
            }
            if (!(sm.options.customElement && sm.options.backdrop)) {
                sm.menuWrapper.attr('id', 'currentDomMenu');
                Promise.resolve().then(() => currentDomMenu = sm);
            }
        });
        return sm;
    }
    setSearch(str) {
        this.$search.val(str).trigger('input');
        this.focus();
    }
    setSearchWithPrefix(str) {
        this.setSearch((this.matchedSource?.prefix || '') + str);
    }

    focus() {
        if (this.options.customElement || this.options.customElement === null) {
            this.menuWrapper[0].focus({ preventScroll: true, focusVisible: false } as any /* types for FocusOptions seems outdated, misses focusVisible */);
        } else {
            (this.menuWrapper.find('input')[0] as HTMLInputElement).focus({ preventScroll: true });
        }
    }
    clear() {
        this[Symbol.dispose]();
    }
    [Symbol.dispose](): void {
        if (this.cancellation) this.cancellation.abort();
        this.menuWrapper.remove();
        this.backdropEl?.remove();
        this.raceCtr++; //cancel any running async iterators
        currentDomMenu = null;
        if (this.options.forResponse) {
            if (this.options.cancelResponse !== undefined) this.promise.resolve(this.options.cancelResponse);
            else  this.promise.reject();
        } else this.promise.resolve(null);
        if (this.parentMenu) {
            currentDomMenu = this.parentMenu;
            if (this.parentMenu instanceof SearchableMenu) this.parentMenu.focus();
            this.parentMenu = undefined;
        } else if (DomWindow.current) {
            DomWindow.current.focus();
        }
    }
    static menuFilter(searchString: string) {
        var searchStrings : string[] = searchString.toLowerCase().split(/\s+/);
        return (itemToCheck: string) => {
            return searchStrings.every(searchString => itemToCheck.toLowerCase().indexOf(searchString) !== -1);
        };
    }
    static filterMenu(itemToCheck: string, searchStringLowercase: string) {
        var searchStrings : string[] = searchStringLowercase.split(/\s+/);
        return searchStrings.every(searchString => itemToCheck.toLowerCase().indexOf(searchString) !== -1);
    }
    _menuFilterCheckFor: string;
    _menuFilterCaseSensitive: boolean;
    _setMenuFilter(searchString: string) {
        this._menuFilterCheckFor = searchString.toLowerCase();
        this._menuFilterCaseSensitive = false;
        if (this._menuFilterCheckFor !== searchString) {
            this._menuFilterCheckFor = searchString;
            this._menuFilterCaseSensitive = true;
        }
    }
    filterMenu(itemToCheck: string) {
        var searchStrings : string[] = this._menuFilterCheckFor.split(/\s+/);
        if (this._menuFilterCaseSensitive)
            return searchStrings.every(searchString => itemToCheck.indexOf(searchString) !== -1);
        else
            return searchStrings.every(searchString => itemToCheck.toLowerCase().indexOf(searchString) !== -1);
    }
    static closeCurrentMenu(closeAll = false) {
        if (closeAll) {
            while (currentDomMenu) currentDomMenu[Symbol.dispose]();
        } else {
            if (currentDomMenu) currentDomMenu[Symbol.dispose]();
        }
    }
}


export class Toolbar {
    menu:JQuery;

    constructor(public horizontal:boolean, public menuItems: MenuItem[] | Command[], public itemClick: ((item, e)=>void) | undefined, rootElement: HTMLElement) {
        this.menu = $(rootElement).addClass('contexttoolbar').addClass(horizontal ? "horizontal" : "vertical");
        this.setButtons(menuItems);
    }
    setButtons(menuItems: MenuItem[] | Command[]) {
        this.menu.html("");
        for(const item of menuItems) {
            this.addButton(item);
        }
    }
    addButton(itemData: Command | MenuItem) {
        const item = (itemData instanceof Command) ? itemData.props : itemData;
        var el = EL('button', {title: item.title + (item.hotkey ? ` [${item.hotkey}]` : ''), appendTo: this.menu[0]});
        renderIcon2(item.icon as string, 32, UiContext.instance.db, el);
        if (this.itemClick) el.addEventListener('click', (e) => this.itemClick(itemData, e));
        else if (item.action instanceof Function) el.addEventListener('click', (e) => item.action(e, itemData));
        else el.style.opacity = "0.5";
        el.addEventListener('contextmenu', function(e) {
            toolbarContextmenu(e, item);
        });
    }
    hide () {
        this.menu.remove();
    }
}
export class ContextToolbar extends Toolbar {
    constructor(horizontal:boolean, menuItems: CommandProperties[] | Command[], itemClick: ((item, e)=>void) | undefined, x:number, y:number) {
        super(horizontal, menuItems, itemClick, EL("div", {class:`absolute wndStack`}));

        bringToFrontWndStack(this.menu[0]);
        $(document.body).append(this.menu);

        if (this.horizontal) {
            if (DomWindowConfig.isMobileMode) {
                const bottomMost = window.innerHeight - this.menu[0].clientHeight;
                this.menu.css({ top: clamp(parseInt(localStorage.horizontalToolbarPos) || (bottomMost - 30), 0, bottomMost) + "px", left: "0", maxWidth: (window.innerWidth) + "px", overflowY: "auto" });

                dragdrop1d(this.menu[0], 'y', [{
                    button: 1, condition: (o, s) => true,
                    init: (x) => { return this.menu[0].offsetTop; },
                    transform: (x, diff) => this.menu[0].style.top = x + 'px',
                    finishMove: (x, diff) => {
                        if(x > bottomMost + 2 || x < 0) this.hide();
                        else localStorage.horizontalToolbarPos = x;
                    }, allowDefault: false, otherAxisCancelRatio: 0.4, dontMoveBeforeLock: true,
                }])
            } else
                this.menu.css({ top: Math.max(0,y-50) + "px", left: Math.max(0,x) + "px", maxWidth: (window.innerWidth - x) + "px", overflowY: "auto" });
        } else {
            this.menu.css({ top: Math.max(0,y) + "px", left: Math.max(0,x-50) + "px", maxHeight: (window.innerHeight - y) + "px", overflowX: "auto" });
        }
        this.menu.fadeIn();
    }
}

export class ProgressBar {
    private static loading : HTMLDivElement = EL('div', { idGetOrCreate: 'statusindicator_progressBar', class: 'progressBar visible' });
    private static loadingText : HTMLDivElement = EL('div', { idGetOrCreate: 'statusindicator_progressBarText', class: 'progressBarText visible' });
    private static timeoutId : number | null = null;
    private static counter = 0;
    private static messageStr = '';
    static show(message?: string): Disposable {
        ProgressBar.loading.classList.add("visible");
        ProgressBar.loadingText.classList.add("visible");
        console.timeLog('boot', message);
        if (message) {
            console.time(message);
            ProgressBar.messageStr += message+ " | ";
            ProgressBar.loadingText.innerText = ProgressBar.messageStr;
        }
        ProgressBar.counter++;
        return {
            disposed: false,
            [Symbol.dispose]() {
                if (this.disposed) return;
                ProgressBar.hide();
                if (message) {
                    console.timeEnd(message);
                    ProgressBar.messageStr = ProgressBar.messageStr.replace(message+ " | ", '');
                    ProgressBar.loadingText.innerText = ProgressBar.messageStr;
                }
                this.disposed = true;
            }
        } as Disposable;
    }
    static hide() {
        if (ProgressBar.counter > 0) ProgressBar.counter--;
        if (ProgressBar.counter > 0) return;
        console.log('hide progressbar');
        if(ProgressBar.timeoutId)window.clearTimeout(ProgressBar.timeoutId);
        ProgressBar.timeoutId = window.setTimeout(function() {
            ProgressBar.loading.classList.remove("visible");
            ProgressBar.loadingText.classList.remove("visible");
        });
    }
}

registerClearMenuHandlers();

